// src/pages/AdminMarketingAnalytics.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '../services/supabaseClient';
import { trackMarketingEvent } from '../lib/marketingEvents';
import {
  FaChartLine,
  FaExternalLinkAlt,
  FaEye,
  FaMousePointer,
  FaRedo,
  FaRoute,
  FaBullseye,
  FaClock,
} from 'react-icons/fa';

type RangeKey = '7d' | '30d' | '90d';

type AnalyticsResponse = {
  ok: boolean;
  range?: RangeKey;
  summary?: {
    total_events: number;
    page_views: number;
    clicks: number;
    outbound_clicks: number;
    unique_sessions: number;
    conversions: number;
  };
  top_pages?: Array<{ path: string; views: number; unique_sessions: number }>;
  top_clicks?: Array<{ label: string; event_type: string; target_url?: string; count: number }>;
  outbound?: Array<{ target_url: string; label: string; count: number }>;
  timeline?: Array<{ day: string; page_views: number; clicks: number; outbound_clicks: number; conversions: number }>;
  recent_events?: Array<any>;
  error?: string;
};

const RANGE_OPTIONS: Array<{ value: RangeKey; label: string }> = [
  { value: '7d', label: '7 jours' },
  { value: '30d', label: '30 jours' },
  { value: '90d', label: '90 jours' },
];

function formatNumber(value: number | undefined) {
  return new Intl.NumberFormat('fr-CA').format(value || 0);
}

function eventLabel(type: string) {
  if (type === 'page_view') return 'Page vue';
  if (type === 'outbound_forfeo_com') return 'Sortie vers Forfeo.com';
  if (type === 'outbound_click') return 'Sortie externe';
  if (type === 'internal_click') return 'Clic interne';
  if (type === 'conversion') return 'Conversion';
  return type || 'Événement';
}

export default function AdminMarketingAnalytics() {
  const [range, setRange] = useState<RangeKey>('30d');
  const [data, setData] = useState<AnalyticsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<string | null>(null);

  const summary = data?.summary || {
    total_events: 0,
    page_views: 0,
    clicks: 0,
    outbound_clicks: 0,
    unique_sessions: 0,
    conversions: 0,
  };

  const conversionRate = useMemo(() => {
    if (!summary.page_views) return '0 %';
    return `${((summary.conversions / summary.page_views) * 100).toFixed(1)} %`;
  }, [summary.conversions, summary.page_views]);

  const loadAnalytics = async () => {
    setLoading(true);
    setMessage(null);
    try {
      const { data: result, error } = await supabase.functions.invoke('admin-marketing-analytics', {
        body: { range },
      });
      if (error) throw error;
      if (!result?.ok) throw new Error(result?.error || 'analytics_unavailable');
      setData(result);
    } catch (error: any) {
      setData(null);
      setMessage(error?.message || 'Impossible de charger les analytics marketing.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range]);

  const sendTestEvent = async () => {
    setMessage(null);
    const result: any = await trackMarketingEvent({
      event_type: 'conversion',
      event_name: 'Test admin analytics',
      label: 'Test conversion admin',
      source: 'admin_marketing_analytics',
      metadata: { module: 'AdminMarketingAnalytics' },
    });
    if (result?.ok) {
      setMessage('Événement test ajouté. Rafraîchis les données dans quelques secondes.');
      setTimeout(loadAnalytics, 800);
    } else {
      setMessage('Le test n’a pas pu être enregistré. Vérifie la fonction track-marketing-event.');
    }
  };

  const cards = [
    { label: 'Pages vues', value: summary.page_views, icon: <FaEye />, tone: 'blue' },
    { label: 'Clics', value: summary.clicks, icon: <FaMousePointer />, tone: 'indigo' },
    { label: 'Vers Forfeo.com / externes', value: summary.outbound_clicks, icon: <FaExternalLinkAlt />, tone: 'cyan' },
    { label: 'Sessions uniques', value: summary.unique_sessions, icon: <FaRoute />, tone: 'emerald' },
    { label: 'Conversions', value: summary.conversions, icon: <FaBullseye />, tone: 'amber' },
    { label: 'Taux conversion', value: conversionRate, icon: <FaChartLine />, tone: 'rose', textValue: true },
  ];

  return (
    <div className="animate-in fade-in space-y-8">
      <div className="relative overflow-hidden rounded-[2rem] border border-slate-800 bg-slate-950 p-6 text-white shadow-2xl shadow-slate-900/20 md:p-8">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(14,165,233,0.35),transparent_36%),radial-gradient(circle_at_bottom_right,rgba(99,102,241,0.28),transparent_35%)]" />
        <div className="relative z-10 flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-cyan-300/20 bg-cyan-300/10 px-4 py-2 text-[10px] font-black uppercase tracking-[0.28em] text-cyan-200">
              <FaChartLine /> Analytics marketing
            </div>
            <h2 className="text-3xl font-black tracking-tight md:text-4xl">Mesurer ce qui convertit vraiment.</h2>
            <p className="mt-3 max-w-2xl text-sm font-medium leading-7 text-slate-300">
              Suis les pages vues, les clics vers Forfeo.com, les CTA importants, les alertes, les cartes cadeaux et les conversions clés de Forfeo Explore.
            </p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row">
            <select
              value={range}
              onChange={(event) => setRange(event.target.value as RangeKey)}
              className="rounded-2xl border border-white/10 bg-white px-4 py-3 text-sm font-black text-slate-900 outline-none"
            >
              {RANGE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
            <button
              type="button"
              onClick={loadAnalytics}
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-white px-5 py-3 text-sm font-black text-slate-950 shadow-lg transition hover:-translate-y-0.5"
            >
              <FaRedo /> Rafraîchir
            </button>
            <button
              type="button"
              onClick={sendTestEvent}
              className="inline-flex items-center justify-center gap-2 rounded-2xl border border-white/20 bg-white/10 px-5 py-3 text-sm font-black text-white transition hover:-translate-y-0.5 hover:bg-white/15"
            >
              <FaBullseye /> Test
            </button>
          </div>
        </div>
      </div>

      {message && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4 text-sm font-bold text-amber-800">
          {message}
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-6">
        {cards.map((card) => (
          <div key={card.label} className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-950 text-lg text-white">
              {card.icon}
            </div>
            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">{card.label}</p>
            <p className="mt-2 text-2xl font-black text-slate-950">{card.textValue ? card.value : formatNumber(card.value as number)}</p>
          </div>
        ))}
      </div>

      {loading ? (
        <div className="rounded-3xl border border-slate-200 bg-white p-8 text-center text-sm font-bold text-slate-500">
          Chargement des données marketing...
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
          <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <h3 className="mb-5 text-xl font-black text-slate-950">Pages qui attirent le plus</h3>
            <div className="space-y-3">
              {(data?.top_pages || []).length === 0 && <p className="text-sm font-bold text-slate-400">Aucune page vue enregistrée pour l’instant.</p>}
              {(data?.top_pages || []).map((item) => (
                <div key={item.path} className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-black text-slate-900">{item.path || '/'}</p>
                      <p className="mt-1 text-xs font-semibold text-slate-500">{formatNumber(item.unique_sessions)} sessions uniques</p>
                    </div>
                    <span className="rounded-full bg-blue-100 px-3 py-1 text-xs font-black text-blue-700">{formatNumber(item.views)} vues</span>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <h3 className="mb-5 text-xl font-black text-slate-950">CTA et clics importants</h3>
            <div className="space-y-3">
              {(data?.top_clicks || []).length === 0 && <p className="text-sm font-bold text-slate-400">Aucun clic enregistré pour l’instant.</p>}
              {(data?.top_clicks || []).map((item, index) => (
                <div key={`${item.label}-${index}`} className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-black text-slate-900">{item.label || 'Clic sans libellé'}</p>
                      <p className="mt-1 text-xs font-semibold text-slate-500">{eventLabel(item.event_type)}</p>
                      {item.target_url && <p className="mt-1 truncate text-[11px] font-medium text-slate-400">{item.target_url}</p>}
                    </div>
                    <span className="rounded-full bg-slate-950 px-3 py-1 text-xs font-black text-white">{formatNumber(item.count)}</span>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <h3 className="mb-5 text-xl font-black text-slate-950">Sorties vers Forfeo.com / externes</h3>
            <div className="space-y-3">
              {(data?.outbound || []).length === 0 && <p className="text-sm font-bold text-slate-400">Aucune sortie enregistrée pour l’instant.</p>}
              {(data?.outbound || []).map((item, index) => (
                <div key={`${item.target_url}-${index}`} className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
                  <p className="text-sm font-black text-slate-900">{item.label || 'Lien externe'}</p>
                  <p className="mt-1 truncate text-xs font-semibold text-slate-500">{item.target_url}</p>
                  <p className="mt-3 text-xs font-black uppercase tracking-widest text-cyan-700">{formatNumber(item.count)} clics</p>
                </div>
              ))}
            </div>
          </section>

          <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <h3 className="mb-5 text-xl font-black text-slate-950">Activité récente</h3>
            <div className="max-h-[430px] space-y-3 overflow-y-auto pr-1">
              {(data?.recent_events || []).length === 0 && <p className="text-sm font-bold text-slate-400">Aucune activité récente.</p>}
              {(data?.recent_events || []).map((event) => (
                <div key={event.id} className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-sm font-black text-slate-900">{eventLabel(event.event_type)}</p>
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold text-slate-400"><FaClock /> {new Date(event.created_at).toLocaleString('fr-CA')}</span>
                  </div>
                  <p className="mt-1 text-xs font-semibold text-slate-600">{event.label || event.event_name || '—'}</p>
                  <p className="mt-1 truncate text-[11px] font-medium text-slate-400">{event.path || event.target_url || '—'}</p>
                </div>
              ))}
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
